import { TranslationsContext } from '@teliaee/telia-translate';
import { observer } from 'mobx-react';
import * as React from 'react';
import { DateField, DatepickerValue, IDateRange, IFormHelperProps } from 'telia-front-react';
import { DATE_AND_TIME_ID, helperInErrorState } from '../../util/FieldHelperUtil';
import { AvailableReservation } from '../../reservation/common/ReservationInterfaces';

const FIFTEEN_MINUTES_IN_MILLIS = 900000;

const getTimeInMillis = (time: string): number => {
  return new Date(time).getTime();
};

const getTime = (time: string): DatepickerValue => {
  return time ? { from: new Date(time), to: new Date(getTimeInMillis(time) + FIFTEEN_MINUTES_IN_MILLIS) } : null;
};

interface DateAndTimeSelectionProps {
  value: string;
  availableTimes: AvailableReservation[];
  onChange: (value: DatepickerValue) => void;
  getHelper: () => IFormHelperProps | undefined;
  shouldValidate: boolean;
  isDisabled?: boolean;
  label?: string;
}

const DateAndTimeSelection = observer((props: DateAndTimeSelectionProps): React.JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { value, availableTimes, onChange, getHelper, shouldValidate, isDisabled, label } = props;
  const [dateAndTimeHelper, setDateAndTimeHelper] = React.useState<IFormHelperProps | undefined>(undefined);

  React.useEffect(() => {
    setDateAndTimeHelper(getHelper());
  }, [value, shouldValidate]);

  const getAvailableTimes = (): IDateRange[] => {
    return availableTimes.map((available: AvailableReservation) => ({
      from: new Date(available.time),
      to: new Date(getTimeInMillis(available.time) + FIFTEEN_MINUTES_IN_MILLIS)
    }));
  };

  return (
    <DateField
      id={DATE_AND_TIME_ID}
      label={label || translate('form.fields.dateAndTime')}
      format="d.MM.yyyy HH:mm"
      formatTo="HH:mm"
      onChange={onChange}
      times={{ options: getAvailableTimes(), helper: dateAndTimeHelper ? { type: 'error', content: dateAndTimeHelper.text } : undefined }}
      value={getTime(value)}
      isInvalid={helperInErrorState(dateAndTimeHelper)}
      errorMessage={dateAndTimeHelper?.text as string}
      datepicker={{
        clearButtonLabel: translate('form.fields.dateAndTime.clearButtonLabel'),
        submitButtonLabel: translate('form.fields.dateAndTime.submitButtonLabel'),
        onClear: () => onChange(null)
      }}
      isDisabled={isDisabled}
    />
  );
});

export default DateAndTimeSelection;
